import { Box, Button, Card, createStyles, Group, Stack, Text, Title } from '@mantine/core'
import { IconClock } from '@tabler/icons'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'

import { CurrencyField } from '@/components/commons/DisplayData'
import { EnrollmentCourseOffering as EnrollmentCourseOfferingType } from '@/types'

interface Props {
  offering: EnrollmentCourseOfferingType
  href: string
}

const durationLabels: Record<string, [string, string]> = {
  hours: ['hora', 'horas'],
  days: ['dia', 'dias'],
  months: ['mês', 'meses']
}

export const formatDuration = (duration?: number | null, durationType?: string) => {
  if (!duration || !durationType) return null

  const [singular, plural] = durationLabels[durationType] || []
  return `${duration} ${(duration === 1 ? singular : plural) || durationType}`
}

const useStyles = createStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.lg,
    '&:hover': {
      backgroundColor: theme.colors.gray[1],
      borderColor: theme.colors[theme.primaryColor][3]
    }
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    aspectRatio: '2 / 1',
    backgroundColor: theme.colors[theme.primaryColor][0]
  },
  body: { flexGrow: 1 },
  badge: {
    alignSelf: 'flex-start',
    backgroundColor: theme.colors[theme.primaryColor][0],
    color: theme.colors[theme.primaryColor][7]
  },
  title: {
    fontSize: theme.fontSizes.lg,
    lineHeight: 1.35
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    whiteSpace: 'pre-line'
  },
  info: {
    color: theme.colors.gray[7],
    svg: { color: theme.colors[theme.primaryColor][6] }
  }
}))

export default function CourseCard({ offering, href }: Props) {
  // Hooks
  const { classes } = useStyles()

  // Constants
  const { course, enrollment } = offering || {}
  const { recurrence, installmentAmount } = enrollment?.institutionContractTemplate || {}
  const duration = formatDuration(course?.duration, course?.durationType)

  return (
    <Card withBorder p={0} className={classes.card}>
      <Card.Section>
        <Box className={classes.imageWrapper}>
          <Link href={href}>
            <Image
              src={course?.image || '/images/placeholders/store.png'}
              alt={course?.name || ''}
              fill
              quality={60}
              style={{ objectFit: 'cover' }}
            />
          </Link>
        </Box>
      </Card.Section>

      <Stack p="md" spacing="xs" className={classes.body}>
        <Title order={4} className={classes.title}>
          {course?.name}
        </Title>

        {!!course?.description && (
          <Text size="sm" color="dimmed" className={classes.description}>
            {course.description}
          </Text>
        )}

        <Stack spacing={4} pt="md" sx={{ marginTop: 'auto' }}>
          {!!duration && (
            <Group spacing={6} className={classes.info}>
              <IconClock size={16} />
              <Text size="xs" weight={500}>
                Duração: {duration}
              </Text>
            </Group>
          )}
        </Stack>

        {!!enrollment?.institutionContractTemplate?.installmentAmount && (
          <Text component="span" color="green" size="lg" fw="bold">
            {Number(recurrence) > 1 ? `${Number(recurrence)}x ` : ''}
            <CurrencyField value={Number(installmentAmount)} />
          </Text>
        )}

        <Button component={Link} href={href} color="green" size="md" fullWidth>
          Ver curso
        </Button>
      </Stack>
    </Card>
  )
}
